// @ts-nocheck
/**
 * Template V2 — data binding, value source, and DataRef types.
 *
 * Contains all discriminated-union binding primitives.
 * No imports from sibling type files — this module has no local dependencies.
 */

/** References a top-level item field by name (e.g. 'name', 'notes'). */
export interface ItemFieldRef {
  /** Discriminant for item field references. */
  kind: 'itemField';
  /** Name of the item field. */
  field: string;
}

/**
 * References a specific index within an item's image URI array.
 * Used by WP-044 indexed image slot bindings.
 */
export interface IndexedItemFieldRef {
  /** Discriminant for indexed item field references. */
  kind: 'indexedItemField';
  /** Array-backed field currently supported by the runtime. */
  field: 'imageUris';
  /** Zero-based index into the image URI array. */
  index: number;
}

/** References a category-backed property by category and property IDs. */
export interface CategoryPropertyRef {
  /** Discriminant for category property references. */
  kind: 'categoryProperty';
  /** ID of the owning category. */
  categoryId: string;
  /** ID of the property within that category. */
  propertyId: string;
  /**
   * Optional DEC-074 cross-item selector. When set, resolve this property on the
   * item referenced by the named `itemVariable` (typically type `itemRef`) on the
   * **current** binding item — not on BindingContext.item itself.
   * Scripts establish the pointer; layout stays declarative. Omit = current item.
   */
  itemFrom?: {
    kind: 'itemVariable';
    key: string;
  };
}

/** References a computed/derived value registered in the template. */
export interface ComputedValueRef {
  /** Discriminant for computed value references. */
  kind: 'computed';
  /** ID of the computed value definition. */
  computedId: string;
}

/** References a transient local-state slot within the template runtime. */
export interface LocalStateRef {
  /** Discriminant for local state references. */
  kind: 'localState';
  /** Key identifying the local state slot. */
  key: string;
}

/** All runtime context keys supplied read-only by the host. */
export type RuntimeContextKey =
  | 'currentDateTime'
  | 'currentUserId'
  | 'privacyLevel'
  | 'authState'
  | 'canvasScale'
  | 'canvasMode'
  | 'colorScheme';

/** References a read-only runtime context value provided by the host. */
export interface RuntimeContextRef {
  /** Discriminant for runtime context references. */
  kind: 'runtimeContext';
  /** Name of the runtime context key. */
  key: RuntimeContextKey;
}

/** Lazy-derived item metrics available as expression inputs (DEC-037). */
export type ItemMetricKey =
  | 'childCount'
  | 'descendantCount'
  | 'containerChildCount'
  | 'containerDescendantCount'
  | 'leafChildCount'
  | 'leafDescendantCount'
  | 'listDepth'
  | 'graphDepth'
  | 'outEdgeCount'
  | 'inEdgeCount'
  | 'imageCount'
  | 'documentCount'
  | 'categoryCount'
  | 'filledCategoryPropertyCount'
  | 'unfilledCategoryPropertyCount'
  | 'tagCount'
  | 'historyEntryCount'
  | 'svgBackgroundBytes'
  | 'hasSvgImage'
  | 'hasPngImage'
  | 'hasCanvasThumb'
  | 'descendantsMaxLastEdited'
  | 'descendantsMaxLastMoved'
  | 'descendantsMaxLastViewed'
  | 'descendantsMinLastEdited'
  | 'descendantsMinLastMoved'
  | 'descendantsMinLastViewed';

/** References a lazy-derived item metric resolved at runtime. */
export interface ItemMetricRef {
  /** Discriminant for item metric references. */
  kind: 'itemMetric';
  /** Metric key to resolve for the current item. */
  metric: ItemMetricKey;
}

/** References another expression in the workspace/template library (nested eval). */
export interface ExpressionLibraryRef {
  /** Discriminant for nested expression references. */
  kind: 'expression';
  /** ID of the expression definition to evaluate. */
  expressionId: string;
}

/** User-editable app chrome color tokens (Settings → Theme colors). */
export type ThemeColorKey =
  | 'background'
  | 'surface'
  | 'text'
  | 'textSecondary'
  | 'onPrimary'
  | 'border'
  | 'borderLight'
  | 'borderDark'
  | 'hover'
  | 'active'
  | 'disabled'
  | 'focus'
  | 'placeholder'
  | 'primary'
  | 'primaryLight'
  | 'secondary'
  | 'secondaryLight'
  | 'success'
  | 'successLight'
  | 'warning'
  | 'warningLight'
  | 'danger'
  | 'dangerLight'
  | 'error'
  | 'errorLight'
  | 'info'
  | 'infoLight'
  | 'accent'
  | 'accentLight'
  | 'itemBackground'
  | 'itemText'
  | 'itemBorder'
  | 'itemDropShadow';

/** App chrome tokens from Settings → Theme colors (excludes item defaults). */
export const APP_THEME_COLOR_KEYS: readonly ThemeColorKey[] = [
  'background',
  'surface',
  'text',
  'textSecondary',
  'onPrimary',
  'border',
  'borderLight',
  'borderDark',
  'hover',
  'active',
  'disabled',
  'focus',
  'placeholder',
  'primary',
  'primaryLight',
  'secondary',
  'secondaryLight',
  'success',
  'successLight',
  'warning',
  'warningLight',
  'danger',
  'dangerLight',
  'error',
  'errorLight',
  'info',
  'infoLight',
  'accent',
  'accentLight',
] as const;

/** Canvas item creation defaults from Containd Defaults (VisualSettings, per light/dark). */
export const ITEM_DEFAULT_THEME_COLOR_KEYS: readonly ThemeColorKey[] = [
  'itemBackground',
  'itemText',
  'itemBorder',
  'itemDropShadow',
] as const;

/** All themeColor ref keys (app chrome + item defaults). */
export const THEME_COLOR_KEYS: readonly ThemeColorKey[] = [
  ...APP_THEME_COLOR_KEYS,
  ...ITEM_DEFAULT_THEME_COLOR_KEYS,
] as const;

/** Palette selection for themeColor refs (DEC-047). */
export type ThemeColorMode = 'auto' | 'light' | 'dark';

/** References a persisted template item variable (DEC-048). */
export interface ItemVariableRef {
  /** Discriminant for item-scoped template variable references. */
  kind: 'itemVariable';
  /** Variable key (literal in bindings/expressions; dynamic in scripts). */
  key: string;
}

/** References a read-only host app theme color (DEC-047). */
export interface ThemeColorRef {
  /** Discriminant for app theme color references. */
  kind: 'themeColor';
  /** Token key within the active or forced palette. */
  key: ThemeColorKey;
  /** When auto (default), follows host colorScheme. */
  mode?: ThemeColorMode;
}

/**
 * True when the current item's element attaches `classId` or an ancestor (DEC-081).
 * Same semantics as host `itemHasClass`. Read-only.
 */
export interface ItemHasClassRef {
  kind: 'itemHasClass';
  classId: string;
}

/**
 * True when the current item's assigned `categories[]` includes `categoryId` (DEC-081).
 * Same semantics as Blockly `containd_item_has_category`. Read-only.
 */
export interface ItemHasCategoryRef {
  kind: 'itemHasCategory';
  categoryId: string;
}

/**
 * True when the current item is based on `elementId` (DEC-081).
 * Same semantics as host `itemHasElement`. Read-only.
 */
export interface ItemHasElementRef {
  kind: 'itemHasElement';
  elementId: string;
}

export type ItemMembershipRef = ItemHasClassRef | ItemHasCategoryRef | ItemHasElementRef;

export const ITEM_MEMBERSHIP_REF_KINDS = [
  'itemHasClass',
  'itemHasCategory',
  'itemHasElement',
] as const;

export type ItemMembershipRefKind = (typeof ITEM_MEMBERSHIP_REF_KINDS)[number];

export function isItemMembershipRef(ref: { kind: string }): ref is ItemMembershipRef {
  return (
    ref.kind === 'itemHasClass'
    || ref.kind === 'itemHasCategory'
    || ref.kind === 'itemHasElement'
  );
}

/** Union of all supported data reference forms. */
export type DataRef =
  | ItemFieldRef
  | IndexedItemFieldRef
  | CategoryPropertyRef
  | ComputedValueRef
  | LocalStateRef
  | RuntimeContextRef
  | ItemMetricRef
  | ExpressionLibraryRef
  | ItemVariableRef
  | ThemeColorRef
  | ItemHasClassRef
  | ItemHasCategoryRef
  | ItemHasElementRef;

/** Declares separate read and write data binding targets for a component. */
export interface DataBinding {
  /** Optional source for reading data into the component. */
  read?: DataRef;
  /** Optional target for writing data from the component. */
  write?: DataRef;
}

/** A value that may be a static literal, a data binding, or an expression result. */
export type ValueSource<T> =
  | { kind: 'static'; value: T }
  | { kind: 'binding'; ref: DataRef }
  | { kind: 'expression'; expressionId: string };

/** A value that may be a static literal or an expression result (no binding variant). */
export type ValueOrExpression<T> =
  | { kind: 'static'; value: T }
  | { kind: 'expression'; expressionId: string };
